import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiClientConstant, ApiConnector, Table } from '@cureskin/api-client';
import { ConnectionService } from '../../../services/connection-service';
import { LocalStorage } from '../../../services/local-storage-service';
import { AppConfig } from '../../app.config';
import { WindowRefService } from '../../../services/window-ref-service';
import { Broadcaster } from '../../../components/broadcaster';
import { InputType } from '../../../../typings/client/input';

@Component({
    selector: 'experiment',
    templateUrl: './experiment-edit.html',
    styleUrls: ['./experiment-edit.scss'],
    standalone: false
})

export class ExperimentEditComponent {
  // local variable
  experiment: any;
  experimentId: string;
  endDate: Date;
  emailIds: string;
  genderOptions: Array<InputType.SelectOption> = [];
  languageOptions: Array<InputType.SelectOption> = [];

  constructor(
    private windowRefService: WindowRefService,
    private route: ActivatedRoute,
    private router: Router,
    private connectionService: ConnectionService,
    public appConfig: AppConfig,
    public broadcasterService: Broadcaster,
    private storage: LocalStorage) { }

  async ngOnInit(): Promise<void> {
    this.experimentId = this.route.snapshot.params.id;
    this.experiment = new Table.Experiment();
    this.initializeOptions();

    if (this.experimentId) {
      this.experiment = await ApiConnector.findOne(Table.Experiment,
        { where: { objectId: this.experimentId } });
      this.endDate = this.experiment.get('endDate');
      this.emailIds = this.experiment.get('emailIds')?.join(',');
      this.loadExperimentFilters();
    }
  }

  private initializeOptions(): void {
    this.genderOptions = [
      { value: 'ALL', display: 'All' },
      { value: ApiClientConstant.User.Gender.MALE, display: 'Male' },
      { value: ApiClientConstant.User.Gender.FEMALE, display: 'Female' },
    ];
    this.languageOptions = [
      { value: ApiClientConstant.LanguageString.EN, display: 'English' },
      { value: ApiClientConstant.LanguageString.HI, display: 'Hindi' },
      { value: ApiClientConstant.LanguageString.MR, display: 'Marathi' },
      { value: ApiClientConstant.LanguageString.KN, display: 'Kannada' },
      { value: ApiClientConstant.LanguageString.TE, display: 'Telugu' },
    ];
  }

  private loadExperimentFilters(): void {
    const genders = this.experiment.get('genders');
    const languagePreferences = this.experiment.get('languagePreferences');

    // this.experiment.set('genders', Array.isArray(genders) && genders.length ? genders[0] : null);
    if (genders?.length) {
      this.experiment.set('genders', genders[0]);
    }

    if (languagePreferences?.length) {
      this.experiment.set('languagePreferences', languagePreferences);
    }
  }

  onEndDateChange(): void {
    this.experiment.set('endDate', this.endDate);
  }

  onGendersChange(selectedValue: string | null): void {
    // 'ALL' means unset -> do not filter by gender
    if (!selectedValue || selectedValue === 'ALL') {
      this.experiment.unset('genders');
    } else {
      this.experiment.set('genders', selectedValue);
    }
  }

  onLanguagePreferencesChange(selectedValues: Array<string>): void {
    // If all options are selected, deselect all (same as showing to all)
    if (selectedValues && selectedValues.length === this.languageOptions.length) {
      this.experiment.unset('languagePreferences');
    }
  }

  private prepareFiltersForSave(): void {
    const gender = this.experiment.get('genders');
    gender
      ? this.experiment.set('genders', [gender])
      : this.experiment.unset('genders');

    const languagePreferences = this.experiment.get('languagePreferences');
    languagePreferences?.length
      ? this.experiment.set('languagePreferences', languagePreferences)
      : this.experiment.unset('languagePreferences');

    const rawMinAge = this.experiment.get('minAge');
    rawMinAge
      ? this.experiment.set('minAge', Number(rawMinAge))
      : this.experiment.unset('minAge');

    const rawMaxAge = this.experiment.get('maxAge');
    rawMaxAge
      ? this.experiment.set('maxAge', Number(rawMaxAge))
      : this.experiment.unset('maxAge');
  }

  async saveExperiment(): Promise<void> {
    const userRoles: Array<string> = this.storage.getJsonValue('userRoles');
    if (!userRoles.includes('admin') || !userRoles.includes('experimentManager')) {
      this.broadcasterService.broadcast('NOTIFY', { message: 'You do not have access to edit or create experiments',
        type: this.appConfig.Shared.Toast.Type.ERROR });
    }
    if (!this.endDate) {
      this.broadcasterService.broadcast('NOTIFY', { message: 'End Date is missing', type: this.appConfig.Shared.Toast.Type.ERROR });
      return;
    }
    this.experiment.set('minHashCode', Number(this.experiment.get('minHashCode')));
    this.experiment.set('maxHashCode', Number(this.experiment.get('maxHashCode')));
    this.experiment.set('emailIds', this.emailIds.split(','));
    this.prepareFiltersForSave();

    try {
      await this.experiment.save();
      this.broadcasterService.broadcast('NOTIFY', { message: 'Saved Successfully', type: this.appConfig.Shared.Toast.Type.SUCCESS });
      this.router.navigate(['/experiment']);
    } catch (error) {
      this.broadcasterService.broadcast('NOTIFY', { message: error.message, type: this.appConfig.Shared.Toast.Type.ERROR });
    }
  }
}
